import { useEffect, useState } from "react";
import { Form, Input, Button } from "@heroui/react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import axiosInstance from "../../../shared/api/axiosInstance";
import FormAlert from "../../../shared/components/form-alert/FormAlert";
import {
  setCookie,
  isRedirectAllowed,
  isSafeInternalPath,
  deleteCookie,
} from "../../../shared/utils/helpers";
import { trackException, trackInfo, LogLevel } from "../../../shared/services/appInsights";
import { AppInsightsActions, AppInsightsComponents, AppInsightsErrorTypes } from "../../../shared/services/appInsightsConstants";

type MessageType = "success" | "danger" | "warning" | "info";

interface LoginFormData {
  emailAddress: string;
  password: string;
}

const Login = () => {
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<MessageType | null>(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // this runs when coming here from other apps after SessionSync run
    deleteCookie("MSILoggedInEmailAddress");
  }, []);

  const handleSubmit = async ({ emailAddress, password }: LoginFormData) => {
    setLoading(true);
    setMessage(null);
    setMessageType(null);

    // Track login attempt
    trackInfo("User login attempt", { 
      emailAddress,
      component: AppInsightsComponents.LOGIN,
      action: AppInsightsActions.LOGIN_ATTEMPT
    });

    try {
      const response = await axiosInstance.post(
        "/token/create",
        {
          emailAddress,
          password,
        },
        { withCredentials: true }
      );

      if (response.status === 200) {
        //Set client cookie for main website
        setCookie("MSILoggedInEmailAddress", emailAddress);
        
        // Track successful login
        trackInfo("User login successful", { 
          emailAddress,
          component: AppInsightsComponents.LOGIN,
          action: AppInsightsActions.LOGIN_SUCCESS
        });
        
        setLoading(false);
        // Redirect to
        const queryParams = new URLSearchParams(location.search);
        const redirectTo = queryParams.get("redirect");

        // If it's a full URL (e.g., https://app2.aaa.com), do:
        if (redirectTo?.startsWith("http") && isRedirectAllowed(redirectTo)) {
          window.location.replace(redirectTo);
        } else if (redirectTo && isSafeInternalPath(redirectTo)) {
          // Otherwise, use internal navigation
          navigate(redirectTo, { replace: true });
        } else {
          navigate("/product-selection", { replace: true });
        }
      }
    } catch (error) {
      // Track login failure
      trackException(
        error as Error,
        LogLevel.Error,
        { 
          emailAddress,
          component: AppInsightsComponents.LOGIN,
          action: AppInsightsActions.LOGIN_FAILURE,
          errorType: axios.isAxiosError(error) ? AppInsightsErrorTypes.API_ERROR : AppInsightsErrorTypes.UNEXPECTED_ERROR,
          statusCode: axios.isAxiosError(error) ? (error.response?.status?.toString() || "unknown") : "unknown"
        }
      );

      if (axios.isAxiosError(error) && error.response?.data) {
        setMessage(error.response.data);
        setMessageType("danger");
      } else {
        setMessage(
          "An unexpected error occurred. Please try again later or contact MSI."
        );
        setMessageType("danger");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = () => {
    setMessage(null);
    setMessageType(null);
  };

  return (
    <div className="form col-span-6 md:col-span-3 lg:col-span-4">
      <div className="grid grid-cols-8">
        <div className="col-span-2 sm:col-span-2 md:col-span-2 lg:col-span-1 xl:col-span-1 2xl:col-span-1">
          <h6 className="whitespace-nowrap">Sign In</h6>
        </div>
        <div className="m-1.5 col-span-8 sm:col-span-8 md:col-span-8 lg:col-span-5 xl:col-span-4 2xl:col-span-3">
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const data = Object.fromEntries(formData);
              handleSubmit({
                emailAddress: data.emailAddress as string,
                password: data.password as string,
              });
            }}
          >
            <Input
              isRequired
              errorMessage="Email address is required"
              label="Email Address"
              labelPlacement="outside"
              name="emailAddress"
              placeholder="Enter your email address"
              type="email"
              classNames={{
                inputWrapper: "rounded-none border",
                label: "label",
              }}
              onChange={handleInputChange}
              disabled={loading}
            />

            <Input
              isRequired
              errorMessage="Password is required"
              label="Password"
              labelPlacement="outside"
              name="password"
              placeholder="Enter your password"
              type="password"
              classNames={{
                inputWrapper: "rounded-none border",
                label: "label",
              }}
              disabled={loading}
            />
            <div className="flex w-full gap-2 justify-between items-center">
              <div className="space-x-4">
                <a
                  className="navigate-link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/account/forgot-password");
                  }}
                >
                  Forgot password?
                </a>
                <a
                  className="navigate-link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/account/register");
                  }}
                >
                  Register
                </a>
              </div>
              <div>
                <Button
                  isDisabled={loading}
                  size="md"
                  className="rounded-none"
                  color="primary"
                  type="submit"
                >
                  {loading ? "Signing you in..." : "Login"}
                </Button>
              </div>
            </div>

            {message && messageType && (
              <FormAlert message={message} messageType={messageType} />
            )}
          </Form>
        </div>
      </div>
    </div>
  );
};

export default Login;
